/**
 * Phase C - Pages combinées "Panne × Ville" (SEO ultra-longue traîne).
 *
 * Génère 216 combinaisons (12 pannes × 18 villes) avec contenu UNIQUE :
 * - URL : /{panne-base}-{city-slug} (ex: /ecran-casse-iphone-chauray)
 * - Chaque combo = page dédiée avec H1 unique, FAQ combinée, route locale,
 *   tarifs panne + accessibilité ville.
 *
 * IMPORTANT anti-doorway pages : ne pas dupliquer 100% du contenu. Chaque
 * page enrichit la panne avec : route d'accès, distance, transport, FAQ
 * spécifique à la combinaison, urgent tip contextualisé pour les habitants.
 */
import { PANNES } from "@/data/pannesData";
import { CITIES } from "@/data/citiesData";

/**
 * Extrait le slug "base" d'une panne en retirant le suffixe "-niort".
 * Ex: "ecran-casse-iphone-niort" → "ecran-casse-iphone"
 */
export const getPanneBaseSlug = (panne) => panne.slug.replace(/-niort$/, "");

/**
 * Slug combo final = base panne + city slug.
 * Ex: ("ecran-casse-iphone", "chauray") → "ecran-casse-iphone-chauray"
 */
export const buildComboSlug = (panneBase, citySlug) => `${panneBase}-${citySlug}`;

/**
 * Génère toutes les combinaisons (12 × 18 = 216).
 * Chaque combo = { slug, panne, city, h1, metaTitle, metaDescription }.
 */
export const ALL_COMBOS = PANNES.flatMap((panne) =>
  CITIES.map((city) => {
    const base = getPanneBaseSlug(panne);
    const slug = buildComboSlug(base, city.slug);
    // H1 et meta dérivés (légèrement différents pour chaque combo)
    const panneShort = panne.titleH1.split(" à Niort")[0].split(" - ")[0];
    const h1 = `${panneShort} pour les habitants de ${city.name}`;
    const metaTitle = `${panneShort} ${city.name} - Réparation Express Mediastore Niort à ${city.distanceKm} km`;
    const metaDescription = `${panneShort} à ${city.distanceKm} km (${city.timeMinutes} min par ${city.accessRoad}). Diagnostic gratuit, intervention en ${panne.durationLabel}, garantie 6 mois. Dès ${panne.priceFrom} €. Mediastore Niort, spécialiste Apple à 200 m de la gare. ☎ 05 49 05 74 90`;
    return { slug, panne, city, h1, metaTitle, metaDescription, panneShort };
  })
);

/**
 * Lookup d'une combinaison par slug.
 * Retourne undefined si introuvable.
 */
export const findComboBySlug = (slug) =>
  ALL_COMBOS.find((c) => c.slug === slug);

/**
 * Top 6 villes les plus proches de Niort (pour maillage depuis PannePage).
 */
export const NEAREST_CITIES = CITIES.slice()
  .sort((a, b) => a.distanceKm - b.distanceKm)
  .slice(0, 6);

/**
 * Top 6 pannes les plus recherchées (pour maillage depuis CityPage).
 */
export const TOP_PANNES_SLUGS = [
  "ecran-casse-iphone-niort",
  "batterie-iphone-niort",
  "desoxydation-niort",
  "connecteur-charge-iphone-niort",
  "face-id-iphone-niort",
  "recuperation-donnees-niort",
];
export const TOP_PANNES = TOP_PANNES_SLUGS
  .map((s) => PANNES.find((p) => p.slug === s))
  .filter(Boolean);
